import { parsePhoneNumber } from "react-phone-number-input";
import {
    OTPConfig,
    OTPDeliveryResult,
    OTPMatchConditionType,
    OTPOperator,
} from "../types/otp.types.ts";

// Helper function to validate phone number
const isValidPhoneNumber = (phoneNumber: string): boolean => {
    try {
        const parsedNumber = parsePhoneNumber(phoneNumber);
        return Boolean(parsedNumber?.isValid());
    } catch {
        return false;
    }
};

// Helper function to get country code from phone number
const getCountryCode = (phoneNumber: string): string | null => {
    try {
        const parsedNumber = parsePhoneNumber(phoneNumber);
        return parsedNumber?.countryCallingCode || null;
    } catch {
        return null;
    }
};

// Helper function to determine OTP delivery method based on phone number
export const getOTPDeliveryMethod = (OTP_CONFIG: OTPConfig, phoneNumber: string): OTPDeliveryResult | null => {
    // Early return for invalid or empty phone number
    if (!OTP_CONFIG || !phoneNumber || !isValidPhoneNumber(phoneNumber)) {
        return null;
    }

    // Early return if OTP is globally disabled
    if (OTP_CONFIG.global_settings.disable_otp) {
        return null;
    }

    // If a specific method is forced globally, use that
    if (OTP_CONFIG.global_settings.force_method) {
        return {
            primary_method: OTP_CONFIG.global_settings.force_method,
            resend_options: [OTP_CONFIG.global_settings.force_method],
        };
    }

    // Get country code for prefix matching
    const countryCode = getCountryCode(phoneNumber);
    if (!countryCode) {
        return null;
    }

    // Check each rule in order
    for (const rule of OTP_CONFIG.delivery_rules) {
        if (!rule.enabled) continue;

        // Handle match_all type
        if (rule.match_condition.type === OTPMatchConditionType.MATCH_ALL) {
            return rule.result;
        }

        // Handle phone_prefix type
        if (rule.match_condition.type === OTPMatchConditionType.PHONE_PREFIX) {
            const { operator, values } = rule.match_condition;

            if (operator === OTPOperator.IN_LIST && values?.includes(countryCode)) {
                return rule.result;
            }
        }
    }

    // If no rules match, use the last rule (which should be the default)
    return OTP_CONFIG.delivery_rules[OTP_CONFIG.delivery_rules.length - 1].result;
}; 